import { useCallback, useEffect, useMemo, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { CirclePlusIcon, CircleMinusIcon, BoxIcon } from 'lucide-react';
import { Controller, Resolver, useForm, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router';

import DropdownList from '@/components/atoms/DropdownList/DropdownList';
import TabPanel from '@/components/atoms/TabPanel/TabPanel';
import {
  PageSubHeader,
  PageSubHeaderActions,
  PageSubHeaderBreadcrumbs,
  PageSubHeaderTabs,
} from '@/components/organisms/PageSubHeader/PageSubHeader';
import {
  extractIntervalsFromHours,
  intervalsObjectToHours,
  frequencyOptions,
  transformHolidaysToOrchestrator,
  getDateFromDayOfYear,
} from '@/components/pages/BusinessHours/utils';
import Layout from '@/components/templates/Layout';
import PageContentWrapper from '@/components/templates/PageContentWrapper';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import useBusinessHours from '@/hooks/useBusinessHours';
import { useDeskId } from '@/hooks/useDeskId';
import usePrompt from '@/hooks/usePrompt';
import { cn } from '@/lib/utils';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectAccountSlug } from '@/redux/session/selectors';
import useSegments from '@/segment/useSegments';
import { actions } from '@/shared/types/permissions';
import { PageName } from '@/shared/types/segment';
import { RootState } from '@/shared/types/state';
import { formatDate, isSameDay } from '@/util/dateUtils';
import { extractNames, generateUniqueName, timezoneMaker } from '@/util/util';
import { businessHoursSchema } from '@/util/validation/schema-builders';

import Holidays from './Holidays';

import styles from './BusinessHour.module.scss';

type Dates = { startDate?: string; endDate?: string };

export type BusinessHoursForm = {
  businessHourName?: string;
  holidays?: { holidayName?: string; dates?: Dates }[];
};
const FIRST_OF_AUGUST = 213;
const FIFTEENTH_OF_AUGUST = 228;

const BusinessHour = () => {
  useSegments(PageName.BUSINESS_HOUR);
  const navigate = useNavigate();
  const deskId = useDeskId();
  const { businessHourId } = useParams();
  const slug = useSelector(selectAccountSlug);
  const { t } = useTranslation();

  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'business_hours', actions.WRITE)
  );

  const [tabIndex, setTabIndex] = useState(0);
  const [updated, setUpdated] = useState(false);
  const {
    businessHours,
    singleBusinessHour,
    updateBusinessHour,
    addBusinessHour,
    addStatus,
    updateStatus,
    isDefaultBusinessHour,
    hours,
    setHours,
    handleFrequencyClick,
    handleFromClick,
    handleToClick,
    handleTimezoneClick,
    setTimezone,
    timezone,
    handleRemoveClick,
    handleAddClick,
    timezoneOptions,
    startTimeOptions,
    endTimeOptions,
    initialHours,
    setInitalHours,
    initialTimezone,
    setInitialTimezone,
  } = useBusinessHours(businessHourId !== 'draft' ? businessHourId : undefined);

  const inputClassName = useMemo(
    () => cn('rounded pl-4', tabIndex === 0 ? 'w-[495px]' : 'w-[300px]'),
    [tabIndex]
  );

  const {
    handleSubmit,
    control,
    formState: { errors, isDirty, isSubmitting, isValid },
    getValues,
    reset,
  } = useForm<BusinessHoursForm>({
    mode: 'onChange',
    defaultValues: { businessHourName: '', holidays: [] },
    resolver: yupResolver(
      businessHoursSchema(deskId, singleBusinessHour?.name)
    ) as unknown as Resolver<BusinessHoursForm>,
  });
  const dirty =
    isDirty ||
    JSON.stringify(hours) !== JSON.stringify(initialHours) ||
    JSON.stringify(timezone) !== JSON.stringify(initialTimezone);

  const [showDatePicker, setShowDatePicker] = useState([]);

  const { fields, append, remove } = useFieldArray({ control, name: 'holidays' });

  useEffect(() => {
    if (singleBusinessHour) {
      setHours(intervalsObjectToHours(singleBusinessHour.intervals));
      setInitalHours(intervalsObjectToHours(singleBusinessHour.intervals));
      setTimezone(timezoneMaker([singleBusinessHour.timezone])[0]);
      setInitialTimezone(timezoneMaker([singleBusinessHour.timezone])[0]);
    }
  }, [setHours, setInitalHours, setInitialTimezone, setTimezone, singleBusinessHour]);

  useEffect(() => {
    if (singleBusinessHour) {
      reset({
        businessHourName: singleBusinessHour.name,
        holidays: singleBusinessHour.holidays.map((holiday) => ({
          holidayName: holiday.name,
          dates: {
            startDate: `${getDateFromDayOfYear(holiday.start)}`,
            endDate: `${getDateFromDayOfYear(holiday.end)}`,
          },
        })),
      });
      setShowDatePicker(
        Array.from({ length: singleBusinessHour.holidays.length }, () => ({ isOpen: false }))
      );
    }
  }, [reset, singleBusinessHour]);

  const onSubmit = useCallback(() => {
    setUpdated(true);
    const { businessHourName, holidays } = getValues();
    if (businessHourId && businessHourId !== 'draft') {
      updateBusinessHour(
        {
          ...singleBusinessHour,
          timezone: timezone?.value,
          name: businessHourName.trim(),
          intervals: extractIntervalsFromHours(hours),
          holidays: transformHolidaysToOrchestrator({ holidays }),
        },
        {
          onSuccess: () => {
            navigate(`/${slug}/environments/${deskId}/business_hours`);
          },
        }
      );
    } else {
      addBusinessHour(
        {
          timezone: timezone?.value,
          name: generateUniqueName(
            businessHourName.trim(),
            extractNames(businessHours.business_hours)
          ),
          intervals: extractIntervalsFromHours(hours),
          holidays: transformHolidaysToOrchestrator({ holidays }),
        },
        {
          onSuccess: () => {
            navigate(`/${slug}/environments/${deskId}/business_hours`);
          },
        }
      );
    }
  }, [
    getValues,
    businessHourId,
    updateBusinessHour,
    singleBusinessHour,
    timezone?.value,
    hours,
    navigate,
    slug,
    deskId,
    addBusinessHour,
    businessHours?.business_hours,
  ]);

  const handleCalendarSave = useCallback((event, index) => {
    event.preventDefault();
    setShowDatePicker((prev) => {
      const newArray = [...prev];
      newArray[index] = { isOpen: false };
      return newArray;
    });
  }, []);

  const handleCreate = useCallback(() => {
    setShowDatePicker((prev) => {
      const newArray = [...prev];
      newArray.push({ isOpen: false });
      return newArray;
    });
    append({
      holidayName: t('business_hours.summer_holidays'),
      dates: {
        startDate: `${getDateFromDayOfYear(FIRST_OF_AUGUST)}`,
        endDate: `${getDateFromDayOfYear(FIFTEENTH_OF_AUGUST)}`,
      },
    });
  }, [append, t]);

  const handleDelete = useCallback(
    (index) => {
      setShowDatePicker((prev) => {
        const newArray = [...prev];
        newArray.splice(index, 1);
        return newArray;
      });
      remove(index);
    },
    [remove]
  );

  usePrompt(dirty && !updated, t('prompts.leave_warning'), undefined, onSubmit);

  const buttonText = useCallback(
    (index: number) => {
      const values = getValues();
      if (values.holidays) {
        const startDate = values.holidays[index]?.dates?.startDate;
        const endDate = values.holidays[index]?.dates?.endDate;

        if (!startDate || !endDate) {
          return null;
        }

        const startDateObj = new Date(startDate);
        const endDateObj = new Date(endDate);

        if (isNaN(startDateObj.getTime()) || isNaN(endDateObj.getTime())) {
          return null;
        }

        return isSameDay(startDateObj, endDateObj) ? (
          <>{formatDate(startDateObj, 'MMM dd')}</>
        ) : (
          <>
            {formatDate(startDateObj, 'MMM dd')}
            {endDate !== null ? (
              <span>
                {' - '}
                {formatDate(endDateObj, 'MMM dd')}
              </span>
            ) : null}
          </>
        );
      }

      // Default fallback dates
      const defaultStartDate = new Date(getDateFromDayOfYear(FIRST_OF_AUGUST));
      const defaultEndDate = new Date(getDateFromDayOfYear(FIFTEENTH_OF_AUGUST));
      return (
        <>
          {formatDate(defaultStartDate, 'MMM dd')} - {formatDate(defaultEndDate, 'MMM dd')}
        </>
      );
    },
    [getValues]
  );

  const render = useCallback(
    (tab: number) => {
      return (
        <>
          <TabPanel value={tab} index={0}>
            <div className={styles.content}>
              {isDefaultBusinessHour && (
                <span className={styles.message}>
                  {t('business_hours.message.first')}
                  <br /> {t('business_hours.message.second')}
                </span>
              )}
              {!isDefaultBusinessHour && (
                <>
                  <span className={styles.title}>name</span>
                  <form autoComplete="off">
                    <Controller
                      render={({ field: { onChange, value, ref } }) => (
                        <Input
                          id="businessHourName"
                          placeholder={t('common.name_placeholder')}
                          className={cn(
                            inputClassName,
                            errors.businessHourName ? 'border-destructive' : 'border-border'
                          )}
                          onChange={onChange}
                          value={value}
                          ref={ref}
                          disabled={!canWrite}
                        />
                      )}
                      control={control}
                      name="businessHourName"
                    />
                  </form>
                  <div className={styles.error}>{errors?.businessHourName?.message}</div>
                </>
              )}
              <span className={styles.title}>{t('business_hours.timezone')}</span>
              <DropdownList
                optionClick={handleTimezoneClick}
                options={timezoneOptions()}
                size="xxlarge"
                searchBar
                selected={timezone.label}
                height="large"
                noGutter
                disabled={!canWrite}
              >
                {timezone.label}
              </DropdownList>

              <span className={styles.title}>{t('business_hours.schedule')}</span>

              {hours.map((hour, index) => {
                return (
                  <div
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${hour.frequency.label}_${hour.from.label}_${hour.to.label}_${index}`}
                  >
                    <div className={styles.time}>
                      <div className={styles.dropdown}>
                        <DropdownList
                          sortBy={false}
                          optionClick={handleFrequencyClick}
                          options={frequencyOptions(t)}
                          selected={hour.frequency.value}
                          renderValue
                          translationPrefix="business_hours.frequency."
                          height="large"
                          noGutter
                          size="large"
                          index={index}
                          disabled={!canWrite}
                        >
                          {t(`business_hours.frequency.${hour.frequency.value}`)}
                        </DropdownList>
                      </div>

                      <div className={styles.dropdown}>
                        <DropdownList
                          sortBy={false}
                          optionClick={handleFromClick}
                          options={startTimeOptions()}
                          selected={hour.from.label}
                          height="large"
                          noGutter
                          size="small"
                          index={index}
                          lowerCase
                          disabled={!canWrite}
                        >
                          {hour.from.label}
                        </DropdownList>
                      </div>

                      <span className={styles.to}>to</span>

                      <div className={styles.dropdown}>
                        <DropdownList
                          sortBy={false}
                          optionClick={handleToClick}
                          options={endTimeOptions(index)}
                          selected={hour.to.label}
                          height="large"
                          noGutter
                          size="small"
                          index={index}
                          lowerCase
                          disabled={!canWrite}
                        >
                          {hour.to.label}
                        </DropdownList>
                      </div>

                      <Button
                        variant="ghost-neutral"
                        size="icon-sm"
                        aria-label={t('common.create')}
                        onClick={canWrite ? handleAddClick : undefined}
                      >
                        <CirclePlusIcon size={16} className="text-muted-foreground" />
                      </Button>

                      {hours.length > 1 && (
                        <Button
                          variant="ghost-neutral"
                          size="icon-sm"
                          aria-label={t('common.delete')}
                          onClick={() => handleRemoveClick(index)}
                        >
                          <CircleMinusIcon size={16} className="text-muted-foreground" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </TabPanel>
          <TabPanel value={tab} index={1}>
            <Holidays
              fields={fields}
              inputClassName={inputClassName}
              handleCreate={handleCreate}
              handleDelete={handleDelete}
              control={control}
              errors={errors}
              setShowDatePicker={setShowDatePicker}
              showDatePicker={showDatePicker}
              buttonText={buttonText}
              handleCalendarSave={handleCalendarSave}
            />
          </TabPanel>
        </>
      );
    },
    [
      isDefaultBusinessHour,
      t,
      control,
      errors,
      handleTimezoneClick,
      timezoneOptions,
      timezone.label,
      canWrite,
      hours,
      fields,
      inputClassName,
      handleCreate,
      handleDelete,
      showDatePicker,
      buttonText,
      handleCalendarSave,
      handleFrequencyClick,
      handleFromClick,
      startTimeOptions,
      handleToClick,
      endTimeOptions,
      handleAddClick,
      handleRemoveClick,
    ]
  );
  const handleCancelClick = useCallback(() => {
    navigate(`/${slug}/environments/${deskId}/business_hours`);
  }, [deskId, navigate, slug]);

  return (
    <Layout>
      <PageSubHeader
        inlineTabs={true}
        breadcrumbs={
          <PageSubHeaderBreadcrumbs>
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to={`/${slug}/environments/${deskId}/context_bundles`}>
                      <div className="flex items-center">
                        <div className="flex h-8 w-8 items-center justify-center">
                          <BoxIcon size={16} className="text-muted-foreground" />
                        </div>
                        <div className="mr-4 ml-2 h-4 w-px bg-border" />
                        {t('common.environments')}
                      </div>
                    </Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to={`/${slug}/environments/${deskId}/business_hours`}>
                      {t('common.business_hours')}
                    </Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage>
                    {singleBusinessHour?.name?.toLowerCase().replace(' business hours', '') ||
                      t('business_hours.header.default')}
                  </BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </PageSubHeaderBreadcrumbs>
        }
        tabs={
          <PageSubHeaderTabs>
            <Tabs value={tabIndex.toString()} className="w-auto">
              <TabsList>
                <TabsTrigger value="0" onClick={() => setTabIndex(0)}>
                  {t('business_hours.header.setup')}
                </TabsTrigger>
                <TabsTrigger value="1" onClick={() => setTabIndex(1)}>
                  {t('business_hours.header.holidays')}
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </PageSubHeaderTabs>
        }
        actions={
          <PageSubHeaderActions>
            <Button size="sm" variant="ghost-neutral" onClick={handleCancelClick}>
              {t('common.cancel')}
            </Button>
            <Button
              size="sm"
              onClick={handleSubmit(onSubmit)}
              isLoading={addStatus === 'pending' || updateStatus === 'pending' || isSubmitting}
              disabled={!isValid || !dirty}
            >
              {t('common.save_and_close')}
            </Button>
          </PageSubHeaderActions>
        }
      />

      <PageContentWrapper readOnly={!canWrite}>{render(tabIndex)}</PageContentWrapper>
    </Layout>
  );
};

export default BusinessHour;
